import type { StoredDocument } from '@/modules/documents'
import type { FileLayoutWithVersion } from '@/modules/file-reader/fileReaderTypes'

export type FiscalDocumentType =
  | 'nfe'
  | 'cte'
  | 'nfse_nacional'
  | 'sped_fiscal'
  | 'sped_contributions'
  | 'xml'
  | 'text'
  | 'other'

export const BUILT_IN_FISCAL_LAYOUT_TYPES: FiscalDocumentType[] = [
  'nfe', 'cte', 'nfse_nacional', 'sped_fiscal', 'sped_contributions',
]

export function hasBuiltInFiscalLayout(type: FiscalDocumentType): boolean {
  return BUILT_IN_FISCAL_LAYOUT_TYPES.includes(type)
}

export function detectFiscalDocumentType(
  rootName: string | null,
  namespaceUri: string | null,
  fileName = '',
  documentKind?: StoredDocument['document_kind'],
): FiscalDocumentType {
  if (documentKind === 'sped_fiscal') return 'sped_fiscal'
  if (documentKind === 'sped_contributions') return 'sped_contributions'
  if (documentKind === 'text') return 'text'
  if (documentKind === 'other') return 'other'

  const root = (rootName || '').toLowerCase()
  const namespace = (namespaceUri || '').toLowerCase()
  const name = fileName.toLowerCase()
  if (root === 'nfe' || root === 'nfeproc' || namespace.includes('portalfiscal.inf.br/nfe')) return 'nfe'
  if (root === 'cte' || root === 'cteproc' || namespace.includes('portalfiscal.inf.br/cte')) return 'cte'
  if (root.includes('nfse') || namespace.includes('nfse') || name.includes('nfse')) return 'nfse_nacional'
  return documentKind === 'xml' || root ? 'xml' : 'other'
}

export function getStoredDocumentType(
  document: Pick<StoredDocument, 'metadata' | 'original_name' | 'document_kind'>,
): FiscalDocumentType {
  return detectFiscalDocumentType(
    typeof document.metadata?.xml_root === 'string' ? document.metadata.xml_root : null,
    typeof document.metadata?.xml_namespace === 'string' ? document.metadata.xml_namespace : null,
    document.original_name,
    document.document_kind,
  )
}

export function getLayoutDocumentType(layout: FileLayoutWithVersion): FiscalDocumentType {
  const presetKey = layout.latest_version?.parser_config?.preset_key
  if (BUILT_IN_FISCAL_LAYOUT_TYPES.includes(presetKey as FiscalDocumentType)) return presetKey as FiscalDocumentType
  return detectFiscalDocumentType(layout.root_name, layout.namespace_uri, '', layout.format === 'xml' ? 'xml' : 'text')
}

export function isLayoutCompatibleWithDocument(layout: FileLayoutWithVersion, document: StoredDocument): boolean {
  const documentType = getStoredDocumentType(document)
  const layoutType = getLayoutDocumentType(layout)
  if (documentType !== layoutType) return false

  if (BUILT_IN_FISCAL_LAYOUT_TYPES.includes(documentType)) {
    return true
  }

  const rootName = typeof document.metadata?.xml_root === 'string' ? document.metadata.xml_root : null
  const namespaceUri = typeof document.metadata?.xml_namespace === 'string' ? document.metadata.xml_namespace : null
  const isStructuredText = layout.format === 'delimited_text' || layout.format === 'fixed_width_text'
  const isTextDocument = ['text', 'sped_fiscal', 'sped_contributions'].includes(document.document_kind)
  return (isStructuredText && isTextDocument)
    || (layout.format === 'xml' && document.document_kind === 'xml'
      && layout.root_name === rootName && layout.namespace_uri === namespaceUri)
}
